import React, { useEffect, useState } from "react";
import { useSelectedPlanContext } from "../../../contexts/SelectedPlanContext";
import { AnimatePresence, motion } from "framer-motion";
import IconWarningMagenta from "../../../images/icons/icon-warning-magenta.svg";
import IconWarningWhite from "../../../images/icons/icon-warning-white.svg";
import { usePageSettings } from "../../../contexts/PageSettingsContext";

const QuickBuyFormInput = ({
	input,
	inputData,
	isQuickBuy = true,
	resources = [],
	translations,
	disabled,
	inputIsRestricted,
}) => {
	const { selectedPlan, changeSelectedPlan } = useSelectedPlanContext();

	if (input === "rack") {
		input = "rack";
	}

	if (input === "prefixes") {
		input = "prefix";
	}

	if (input === "energies") {
		input = "energy";
	}
	if (input === "bandwidths" || input === "bandwidth") {
		input = "bandwidth";
	}

	let inputIsSelected = selectedPlan?.[input]?.id === inputData.id;

	const handleClick = (event, input, inputData) => {
		event.preventDefault();
		let modifiedInput = {
			id: inputData.id,
			value: inputData.value,
		};

		changeSelectedPlan((prevState) => ({
			...prevState,
			name: "custom",
			[input]: modifiedInput,
		}));
	};

	useEffect(() => {
		// If the selected input is no longer valid due to changed dependencies, find the next valid option.
		if (disabled || inputIsRestricted) {
			const firstValidOption = resources.find((resource) =>
				resource.dependencies?.some((depId) =>
					Object.values(selectedPlan).some((selectedResource) => selectedResource?.id === depId)
				)
			);

			if (firstValidOption) {
				// console.log("## SETTING VALID OPTION ## ", firstValidOption);
				changeSelectedPlan((prevState) => ({
					...prevState,
					[input]: {
						id: firstValidOption.id,
						value: firstValidOption.value,
					},
				}));
			}
		}
	}, [disabled, inputIsSelected]);

	return (
		<button
			onClick={(event) => handleClick(event, input, inputData)}
			disabled={disabled}
			className="w-full transition-all duration-300 relative"
		>
			{isQuickBuy ? (
				<StyledQuickBuyInput selected={inputIsSelected} disabled={disabled} translations={translations}>
					{inputData.value}
				</StyledQuickBuyInput>
			) : (
				<StyledTableInput selected={inputIsSelected} disabled={disabled} translations={translations}>
					{inputData.value}
				</StyledTableInput>
			)}
		</button>
	);
};

const StyledQuickBuyInput = ({ children, selected, disabled, translations }) => {
	const [showPopup, setShowPopup] = useState(false);

	const handleMouseOver = () => {
		if (disabled) {
			setShowPopup(true);
		}
	};

	const handleMouseLeave = () => {
		setShowPopup(false);
	};

	return (
		<>
			<div
				className={`quickbuy-input !font-normal
      ${disabled && "opacity-25 !cursor-not-allowed "}
      ${selected && "selected-quickbuy-input !font-bold"}`}
				onMouseOver={handleMouseOver}
				onMouseLeave={handleMouseLeave}
			>
				{children}
			</div>
			{disabled && showPopup && (
				<AnimatePresence>
					<motion.div
						initial={{ opacity: 0, y: 0 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: 0 }}
						className="absolute z-[999] flex justify-center items-center w-full max-h-[99%] bottom-0 left-0 flex-col gap-2 bg-ipxon-gradient-black-lighter text-center rounded-lg border-gray-100 text-ipxon-light-blue-gray shadow text-ipxonGray text-xs py-3"
						onMouseOver={handleMouseOver}
						onMouseLeave={handleMouseLeave}
					>
						<div className="flex items-center justify-center text-white">
							<img className="text-ipxon-magenta h-4 w-4" src={IconWarningWhite} alt="warning Icon" />
							<span className="ml-2">{translations["ResourceNotAvailable"]}</span>
						</div>
					</motion.div>
				</AnimatePresence>
			)}
		</>
	);
};

const StyledTableInput = ({ children, selected, disabled }) => {
	const [showPopup, setShowPopup] = useState(false);
	const { translations } = usePageSettings();

	const handleMouseOver = () => {
		if (disabled) {
			setShowPopup(true);
		}
	};

	const handleMouseLeave = () => {
		setShowPopup(false);
	};
	return (
		<>
			<div
				className={`table-input !font-normal
          ${disabled && "opacity-25 !cursor-not-allowed "}
          ${selected && "selected-table-input !font-bold"}`}
				onMouseOver={handleMouseOver}
				onMouseLeave={handleMouseLeave}
			>
				{children}
			</div>
			{disabled && showPopup && (
				<AnimatePresence>
					<motion.div
						initial={{ opacity: 0, y: 0 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: 0 }}
						className="absolute z-[999] flex justify-center items-center w-full max-h-[99%] bottom-0 left-0 flex-col gap-2 bg-white text-center rounded-lg border-gray-100 text-ipxon-light-blue-gray shadow text-ipxonGray text-xs py-3"
						onMouseOver={handleMouseOver}
						onMouseLeave={handleMouseLeave}
					>
						<div className="flex items-center justify-center text-ipxon-magenta">
							<img className="text-ipxon-magenta h-4 w-4" src={IconWarningMagenta} alt="warning Icon" />
							<span className="ml-2">{translations["ResourceNotAvailable"]}</span>
						</div>
					</motion.div>
				</AnimatePresence>
			)}
		</>
	);
};

export { QuickBuyFormInput };
